"use client"

import React, {Suspense, useMemo, useState} from "react";
import {useIsMobile} from "@/hooks/use-mobile";
import {cn} from "@/lib/utils";
import {Link} from '@/i18n/navigation';
import {Skeleton} from "@/app/components/common/app-skeleton/AppSkeleton";
import {MainCtaBtn} from "@/app/components/common/main-cta-btn/MainCtaBtn";
import {useGetProvidersInfiniteQuery} from "@/lib/api/games/gameApi";
import {AppFooter} from "@/app/components/common/app-footer/AppFooter";
import LinkedGoBackHeader from "@/app/components/common/go-back-header/LinkedGoBackHeader";
import SearchInput from "@/app/components/common/search-input/SearchInput";
import PageTitle from "@/app/components/common/page-title/PageTitle";
import UsersSvg from "@/app/components/icons/UsersSvg";
import NothingFound from "@/app/components/common/nothing-found/NothingFound";
import ProviderCard from "@/app/components/game/ProviderCard";
import {useTranslations} from "next-intl";
import {useDebounce} from "use-debounce";

export default function Providers() {
    const isMobile = useIsMobile();
    const t = useTranslations();

    const [search, setSearch] = useState('');
    const [debouncedSearchTerm] = useDebounce(search, 500);

    const {
        data: providers,
        isFetching: providersFetching,
        hasNextPage: providersHasNextPage,
        fetchNextPage: providersFetchNextPage
    } = useGetProvidersInfiniteQuery({
        size: 200,
        search: debouncedSearchTerm.trim() ? debouncedSearchTerm.trim() : undefined
    })

    const allProviders = useMemo(() => {
        return providers?.pages?.flatMap(page => page.content || []) || [];
    }, [providers])

    return (
        <div className={'px-[16px] pt-[24px] md:px-[32px] bg-dark-gray scroll-mt-[100px]'}>

            {isMobile ? <LinkedGoBackHeader className={'gap-[12px] mb-[16px]'}
                                            title={t('Providers.providers')}/> : null}
            <div className={cn('mb-[16px] sm:mb-[24px]', {['flex justify-between items-center ']: !isMobile})}>
                {!isMobile && <PageTitle title={t('Providers.providers')} icon={<UsersSvg/>}/>}
                <SearchInput value={search} onChange={(value) => setSearch(value.target.value)}
                             className={'gap-[8px] px-[12px] flex-1 md:h-[41px] md:basis-[395px] md:grow-0'}/>
            </div>

            {
                !allProviders.length && !providersFetching
                ? (
                    <div
                        className={'h-[calc(100vh-271px)] sm:h-[calc(100vh-263px)] md:h-[calc(100vh-172px)] flex items-center justify-center'}>
                        <NothingFound/>
                    </div>
                ) : null
            }

            {
                !providersFetching ? (
                    <div className={'min-h-dvh'}>
                        <div
                            className={'grid grid-cols-3 sm:grid-cols-4 md:grid-cols-4 lg:grid-cols-6 gap-[16px]'}>
                            {allProviders.map((item) => (
                                <Link prefetch={false} key={item.id} href={`/casino/providers/${item.title.toLowerCase().replaceAll(" ", "-")}`}>
                                    <ProviderCard poster={item.previewUrl} name={item.name} sizes={'130px'}
                                                  className={'w-full'}/>
                                </Link>
                            ))}
                        </div>
                    </div>
                ) : null
            }

            {
                providersFetching && (
                    <div
                        className={'grid grid-cols-3 sm:grid-cols-4 md:grid-cols-4 lg:grid-cols-6 gap-[16px]'}>
                        {
                            Array.from({length: 50}).map((_, index) => (
                                <Skeleton key={index} className={'aspect-video bg-noble-black-600'}/>
                            ))
                        }
                    </div>

                )
            }

            {
                providersHasNextPage && (
                    <MainCtaBtn onClick={providersFetchNextPage}
                                className={'mx-auto block w-[280px] h-[41px] py-[12px]'}>
                        {t('AppBtn.loadMore')}
                    </MainCtaBtn>
                )
            }

            <Suspense fallback={null}>
                <AppFooter/>
            </Suspense>

        </div>
    )
}
